const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 4_000;
const MAX_STRING_LENGTH = 500;
const MAX_ARRAY_LENGTH = 20;
const MAX_OBJECT_KEYS = 30;
const MAX_DEPTH = 4;

const BLOCKED_LOG_KEYS = new Set([
  "plugins",
  "pluginList",
  "override",
  "parsers",
  "parser",
  "shortcuts",
  "handlers",
  "transforms",
  "render",
  "inject",
  "dependencies",
  "optionsStore",
  "store",
  "editor",
  "children",
  "emojis",
  "sheet",
  "aliases",
]);

const isBlockedKey = (key: string): boolean => {
  if (BLOCKED_LOG_KEYS.has(key)) {
    return true;
  }

  return key.startsWith("__");
};

/**
 * Marca o que a limpeza esvaziou por completo. O documento do editor chega
 * como `value: [{ children: [...] }]`: `children` é bloqueado, e sem isto
 * sobraria a casca `value: [{}]` no incidente — um campo que aparenta ter
 * conteúdo e não tem, enganando quem investiga.
 *
 * Só vale para o que ficou vazio *depois* da limpeza. Um `[]` ou `{}` que
 * chegou vazio da aplicação é informação de verdade ("a lista estava vazia") e
 * continua no snapshot.
 */
const EMPTIED = Symbol("emptied");

const isEmptied = (value: unknown): boolean => value === EMPTIED;

const truncateString = (value: string, maxLength = MAX_STRING_LENGTH): string => {
  if (value.length <= maxLength) {
    return value;
  }

  return `${value.slice(0, maxLength)}…[+${value.length - maxLength} chars]`;
};

const sanitizeUnknown = (
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>(),
): unknown => {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string") {
    return truncateString(value);
  }

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return value;
  }

  if (typeof value === "function") {
    return "[Function]";
  }

  if (typeof value === "symbol") {
    return value.toString();
  }

  if (depth >= MAX_DEPTH) {
    return "[MaxDepth]";
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncateString(value.message, MAX_MESSAGE_LENGTH),
      stack: value.stack ? truncateString(value.stack, MAX_STACK_LENGTH) : undefined,
    };
  }

  if (Array.isArray(value)) {
    const items = value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => sanitizeUnknown(item, depth + 1, seen))
      .filter((item) => !isEmptied(item));

    // Array cortado mantém a contagem original mesmo sem sobrar item: saber
    // que eram 5.000 nós é diagnóstico, ainda que nenhum possa ser mostrado.
    if (value.length > MAX_ARRAY_LENGTH) {
      return {
        type: "array",
        length: value.length,
        preview: items,
      };
    }

    return value.length > 0 && items.length === 0 ? EMPTIED : items;
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }

    seen.add(value);

    const record = value as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};
    const keys = Object.keys(record).filter((key) => !isBlockedKey(key));

    for (const key of keys.slice(0, MAX_OBJECT_KEYS)) {
      const sanitizedValue = sanitizeUnknown(record[key], depth + 1, seen);
      if (!isEmptied(sanitizedValue)) {
        sanitized[key] = sanitizedValue;
      }
    }

    if (keys.length > MAX_OBJECT_KEYS) {
      sanitized.__truncatedKeys = keys.length - MAX_OBJECT_KEYS;
    }

    const hadContent = Object.keys(record).length > 0;
    return hadContent && Object.keys(sanitized).length === 0
      ? EMPTIED
      : sanitized;
  }

  return truncateString(String(value));
};

export const sanitizeObservabilityMessage = (message: string): string =>
  truncateString(message.trim() || "Erro não tratado no browser", MAX_MESSAGE_LENGTH);

export const sanitizeObservabilityStack = (stack?: string): string | undefined => {
  if (!stack) {
    return undefined;
  }

  return truncateString(stack, MAX_STACK_LENGTH);
};

export const sanitizeObservabilityLogSnapshot = (
  snapshot?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!snapshot) {
    return undefined;
  }

  const sanitized = sanitizeUnknown(snapshot);
  if (isEmptied(sanitized)) {
    return undefined;
  }

  const record = sanitized as Record<string, unknown>;
  return Object.keys(record).length > 0 ? record : undefined;
};

export const collectClientDeviceContext = (): Record<string, unknown> => {
  if (typeof window === "undefined") {
    return {};
  }

  const viewport = `${window.innerWidth}x${window.innerHeight}`;

  return {
    viewport,
    devicePixelRatio: window.devicePixelRatio,
    language: navigator.language,
    platform: navigator.platform,
    userAgent: truncateString(navigator.userAgent, 300),
  };
};

const REACT_HYDRATION_MISMATCH =
  /(?:Minified React error #418|Hydration failed because the server rendered)/i;

export const isIgnorableClientError = (input: {
  message: string;
  filename?: string;
}): boolean => {
  const message = input.message.trim();

  if (!message) {
    return true;
  }

  if (/^ResizeObserver loop/i.test(message)) {
    return true;
  }

  if (message === "Script error." && !input.filename) {
    return true;
  }

  if (REACT_HYDRATION_MISMATCH.test(message)) {
    return true;
  }

  return false;
};
